import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { LoadingState, ErrorState } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { fmtMoney } from "@/lib/format";
import { t } from "@/i18n";

export function IntervalToggle({ value, onChange, testId = "interval-toggle" }) {
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid={testId}>
      {["monthly", "yearly"].map((k) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-200 ${value === k ? "bg-[#0A2540] text-white" : "text-slate-600 hover:text-slate-900"}`}>
          {t(`p7.pricing.${k}`)}
        </button>
      ))}
    </div>
  );
}

export const planPrice = (p, interval) => (interval === "yearly" ? p.price_yearly : p.price_monthly);

export function planFeatureLines(p) {
  const gb = p.photo_quota_mb >= 1024;
  const lines = [t("p7.pricing.users", { n: p.max_internal_users }), gb ? t("p7.pricing.storage", { n: Math.round(p.photo_quota_mb / 1024) }) : t("p7.pricing.storageMb", { n: p.photo_quota_mb })];
  return lines.concat(Object.keys(p.features || {}).filter((k) => p.features[k]).map((k) => t(`plans.flag.${k}`).replace(/ \(.*\)$/, "")));
}

function PlanCard({ plan, interval, featured, index }) {
  const price = planPrice(plan, interval);
  return (
    <div className={`gf-rise relative flex flex-col rounded-2xl border bg-white p-8 ${featured ? "border-[#0A2540] shadow-xl" : "border-slate-200 shadow-sm"}`} style={{ animationDelay: `${index * 90}ms` }} data-testid={`pricing-plan-${plan.name.toLowerCase()}`}>
      <p className="font-display text-2xl font-extrabold text-[#0A2540]">{plan.name}</p>
      <p className="mt-2 min-h-[40px] text-sm text-slate-600">{plan.description}</p>
      <div className="mt-6 flex items-baseline gap-1">
        {price ? (<>
          <span className="font-display text-5xl font-extrabold tracking-tight text-slate-900" data-testid="pricing-plan-price">{fmtMoney(price)}</span>
          <span className="text-sm text-slate-500">{t(interval === "yearly" ? "p7.pricing.perYear" : "p7.pricing.perMonth")} · {t("p7.pricing.exGst")}</span>
        </>) : <span className="text-sm text-slate-500">{t("p7.pricing.notOnInterval")}</span>}
      </div>
      <p className="mt-3 text-sm font-semibold text-teal-700">{t("p7.pricing.trial", { n: plan.trial_days })} · {plan.card_required ? t("p7.pricing.card") : t("p7.pricing.noCard")}</p>
      <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
        {planFeatureLines(plan).map((l) => <li key={l} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />{l}</li>)}
      </ul>
      <div className="mt-auto pt-8">
        {price ? (
          <Button asChild size="lg" variant={featured ? "default" : "outline"} className="h-12 w-full" data-testid={`pricing-start-${plan.name.toLowerCase()}`}>
            <Link to={`/signup?plan=${plan.id}&interval=${interval}`}>{t("p7.pricing.startTrial")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        ) : <Button disabled size="lg" variant="outline" className="h-12 w-full">{t("p7.pricing.notOnInterval")}</Button>}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { data, error, loading, reload } = useFetch("/public/plans");
  const platform = useFetch("/public/platform");
  const [interval, setInterval] = useState("monthly");
  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      <PublicHeader />
      <section className="gf-grid-bg">
        <div className="mx-auto max-w-6xl px-5 pb-10 pt-16 sm:pt-20">
          <p className="gf-rise text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{t("p7.pricing.eyebrow")}</p>
          <h1 className="gf-rise mt-4 max-w-3xl text-4xl font-extrabold tracking-tight text-[#0A2540] sm:text-5xl lg:text-6xl">{t("p7.pricing.title")}</h1>
          <p className="gf-rise mt-6 max-w-xl text-base text-slate-600 md:text-lg">{t("p7.pricing.subtitle")}</p>
          <div className="mt-10"><IntervalToggle value={interval} onChange={setInterval} /></div>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-5 pb-20">
        {loading && !data && <LoadingState />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data?.length === 0 && <p className="gf-card p-8 text-slate-600" data-testid="pricing-empty">{t("p7.pricing.empty")}</p>}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data?.map((p, i) => <PlanCard key={p.id} plan={p} interval={interval} featured={data.length > 1 && i === 1} index={i} />)}
        </div>
        <p className="mt-10 text-sm text-slate-600">{t("p7.pricing.haveAccount")} <Link to="/login" className="font-semibold text-teal-700 hover:text-teal-900" data-testid="pricing-login-link">{t("landing.signIn")}</Link></p>
      </section>
      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500" data-testid="pricing-footer">
        {platform.data?.legal_name || platform.data?.business_name || "GarageFlow"}{platform.data?.abn ? ` · ABN ${platform.data.abn}` : ""}{platform.data?.support_email ? ` · ${platform.data.support_email}` : ""}
      </footer>
    </div>
  );
}
